import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Header from "./components/Header";
import Hero from "./components/Hero";
import { TopArtistSpotlight, LeaderboardRace } from "./components/TopArtistSpotlight";
import Services from "./components/Services";
import BookingSection from "./components/BookingSection";
import Engineers from "./components/Engineers";
import Gallery from "./components/Gallery";
import Equipment from "./components/Equipment";
import StudioRules from "./components/StudioRules";
import RecordingPricing from "./components/RecordingPricing";
import LocationInfo from "./components/LocationInfo";
import StickyBookBar from "./components/StickyBookBar";
import { initBookingClickTracking } from "./utils/bookingAnalytics";
import About from "./components/About";
import Reviews from "./components/Reviews";
import SignupSection from "./components/SignupSection";
import SignUpBanner from "./components/SignUpBanner";
import EarlyBirdBanner from "./components/EarlyBirdBanner";
import FloatingSignUp from "./components/FloatingSignUp";
import FAQ from "./components/FAQ";
import Footer from "./components/Footer";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import Chatbot from "./components/Chatbot";
import VideoShowcase from "./components/VideoShowcase";
import IntroSplash from "./components/IntroSplash";
import { IS_HALLOWEEN } from "./theme";
import { HalloweenEffects } from "./components/Halloween/HalloweenEffects";
import MergeAccount from "./pages/MergeAccount";
import LoyaltyNotifications from "./components/LoyaltyNotifications";
import NotificationPrompt from "./components/NotificationPrompt";
import CreditReminderBanner from "./components/CreditReminderBanner";
import CreditReminderModal from "./components/CreditReminderModal";
import Admin from "./pages/Admin";
import AdminSetup from "./pages/AdminSetup";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import EmployeeSetup from "./pages/EmployeeSetup";
import UploadPortal from "./pages/UploadPortal";
import SessionUploadPortal from "./pages/SessionUploadPortal";
import Profile from "./pages/Profile";
import CustomBooking from "./pages/CustomBooking";
import PayBalance from "./pages/PayBalance";
import InterviewSelect from "./pages/InterviewSelect";
import DealBooking from "./pages/DealBooking";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Join from "./pages/Join";
import BookNow from "./pages/BookNow";
import ArtistPlan from "./pages/ArtistPlan";
import RescheduleSelect from "./components/RescheduleSelect";
import SignupRedirect from "./pages/SignupRedirect";
import ResetPassword from "./pages/ResetPassword";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import CompleteProfilePrompt from "./components/Auth/CompleteProfilePrompt";
import { Toaster } from "./components/ui/sonner";

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Handle old reset_token format - redirect to new reset page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('reset_token');
    if (resetToken) {
      window.location.href = `/reset-password?token=${resetToken}`;
    }
  }, []);

  const handleSignUpClick = () => {
    // Trigger the auth modal to open in signup mode
    window.dispatchEvent(new CustomEvent('openAuthModal', { detail: { mode: 'signup' } }));
  };

  return (
    <div className="min-h-screen bg-black w-full m-0 p-0 overflow-x-hidden">
      <EarlyBirdBanner />
      <Header onMenuToggle={setIsMenuOpen} />
      <Hero />
      <RecordingPricing />
      <TopArtistSpotlight />
      <LeaderboardRace />
      <SignUpBanner onSignUpClick={handleSignUpClick} />
      <Services />
      <Gallery />
      <VideoShowcase />
      <Engineers />
      <BookingSection />
      <LocationInfo />
      <Reviews />
      <Equipment />
      <StudioRules />
      <SignupSection />
      <FAQ />
      <About />
      <Footer />
      <FloatingSignUp />
      <StickyBookBar />
    </div>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);

  useEffect(() => { initBookingClickTracking(); }, []);

  // Check if user needs to complete profile on mount and show credit modal
  useEffect(() => {
    const checkProfileCompletion = () => {
      const userData = localStorage.getItem('user_data');
      const profileCompleted = localStorage.getItem('profile_completed');
      const profileSkipped = localStorage.getItem('profile_prompt_skipped');
      
      if (userData && userData !== 'undefined' && userData !== 'null') {
        try {
          const user = JSON.parse(userData);
          setCurrentUser(user);
          
          // Store user email for credit banner
          if (user.email) {
            localStorage.setItem('user_email', user.email);
          }
          
          if (!profileCompleted && !profileSkipped) {
            // Check if profile is incomplete (missing key fields)
            const isIncomplete = !user.artist_name || !user.full_name || !user.phone;
            
            if (isIncomplete) {
              // Show prompt after a short delay
              setTimeout(() => {
                setShowProfilePrompt(true);
              }, 1000);
            }
          }
          
          // Show credit modal for returning users (after profile check)
          const lastCreditModalShown = localStorage.getItem('credit_modal_last_shown');
          const now = new Date();
          const showModal = !lastCreditModalShown || 
            (now - new Date(lastCreditModalShown)) > 7 * 24 * 60 * 60 * 1000; // 7 days
          
          if (showModal && !profileSkipped && profileCompleted) {
            setTimeout(() => {
              setShowCreditModal(true);
              localStorage.setItem('credit_modal_last_shown', now.toISOString());
            }, 3000); // Show after 3 seconds
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    };
    
    checkProfileCompletion();
  }, []);

  const handleProfileComplete = (updatedUser) => {
    setCurrentUser(updatedUser);
  };

  const handleCreditModalClose = () => {
    setShowCreditModal(false);
  };

  return (
    <div className="App">
      {showSplash && <IntroSplash onComplete={() => setShowSplash(false)} />}
      {IS_HALLOWEEN && <HalloweenEffects />}
      
      {/* Credit Reminder Banner - Shows for logged-in users with credits */}
      {currentUser && <CreditReminderBanner />}
      
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin-setup" element={<AdminSetup />} />
          <Route path="/employee" element={<EmployeeDashboard />} />
          <Route path="/employee-setup" element={<EmployeeSetup />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/upload/:token" element={<UploadPortal />} />
          <Route path="/session-upload/:token" element={<SessionUploadPortal />} />
          <Route path="/custom-booking/:token" element={<CustomBooking />} />
          <Route path="/pay-balance/:bookingId" element={<PayBalance />} />
          <Route path="/interview-select/:token" element={<InterviewSelect />} />
          <Route path="/deal/:token" element={<DealBooking />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/join" element={<Join />} />
          <Route path="/signup" element={<Join />} />
          <Route path="/book" element={<BookNow />} />
          <Route path="/artist-plan" element={<ArtistPlan />} />
          <Route path="/book-now" element={<BookNow />} />
          <Route path="/reschedule-select/:token" element={<RescheduleSelect />} />
          <Route path="/signup" element={<SignupRedirect />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/merge-account/:token" element={<MergeAccount />} />
        </Routes>
        
        {/* Profile Completion Prompt */}
        {showProfilePrompt && currentUser && (
          <CompleteProfilePrompt
            user={currentUser}
            onClose={() => setShowProfilePrompt(false)}
            onComplete={handleProfileComplete}
          />
        )}
        
        {/* Credit Reminder Modal - Shows periodically for returning users */}
        {currentUser && (
          <CreditReminderModal
            userEmail={currentUser.email}
            isOpen={showCreditModal}
            onClose={handleCreditModalClose}
          />
        )}
      </BrowserRouter>
      <Chatbot />
      <PWAInstallPrompt />
      <LoyaltyNotifications />
      {/* Only show notification prompt for logged-in users */}
      {currentUser && (
        <NotificationPrompt 
          userEmail={currentUser.email} 
          userName={currentUser.full_name || currentUser.name} 
        />
      )}
      <Toaster />
    </div>
  );
}

export default App;
